import {
  ArrowRight,
  AudioLines,
  CalendarClock,
  Check,
  LockKeyhole,
  MessageSquareText,
  PenLine,
  Sparkles,
  Target,
  Type,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Brand } from '../components/Brand';
import {
  EMPTY_ONBOARDING_PROFILE,
  getOnboardingQuestion,
  interpretDeterministicTurn,
  normalizeProfile,
  withRecommendedDefaults,
  type OnboardingField,
  type OnboardingFocus,
  type OnboardingMessage,
  type OnboardingTurnRequest,
  type OnboardingTurnResponse,
} from '../lib/onboarding';
import type { OnboardingProfile } from '../types';

interface OnboardingProps {
  onComplete: (profile: OnboardingProfile) => void | Promise<void>;
  onOpenDemo: () => void;
  onTurn?: (request: OnboardingTurnRequest) => Promise<OnboardingTurnResponse>;
}

type Stage = 0 | 1 | 2 | 3;

const STAGES = [
  {
    label: 'Goal',
    title: 'What should social media do for you?',
    subtitle: 'Tell us in your own words. One sentence is enough.',
  },
  {
    label: 'Brand',
    title: 'Tell me about the brand',
    subtitle: 'A name and a few plain words are enough.',
  },
  {
    label: 'Style',
    title: 'How should your content feel?',
    subtitle: 'Describe it, pick one, or let ThisBot recommend it.',
  },
  {
    label: 'Ready',
    title: 'Your social workspace is ready',
    subtitle: 'ThisBot will keep working with you in chat. This opens your conversation, not a dashboard.',
  },
] as const;

const LOOKS_RIGHT = 'Looks right';
const USE_DIRECTION = 'Use this direction';

function initialProfile(): OnboardingProfile {
  return normalizeProfile({
    ...EMPTY_ONBOARDING_PROFILE,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Phoenix',
  });
}

function hasBrand(profile: OnboardingProfile) {
  return Boolean(profile.brandName && profile.businessType);
}

function hasStyle(profile: OnboardingProfile) {
  return Boolean(profile.voice && profile.cadence);
}

function businessTypePhrase(value: string) {
  const kind = value.trim().toLowerCase();
  if (/^(?:a|an|the)\b/.test(kind)) return kind;
  return `${/^[aeiou]/.test(kind) ? 'an' : 'a'} ${kind}`;
}

function stageFocus(stage: Stage, profile: OnboardingProfile): OnboardingFocus {
  if (stage === 0) return 'goal';
  if (stage === 1) return profile.brandName ? 'businessType' : 'brandName';
  if (stage === 2) return profile.voice ? 'cadence' : 'voice';
  return 'review';
}

function stageAssistant(stage: Stage, profile: OnboardingProfile) {
  if (stage === 1 && hasBrand(profile)) {
    return `I heard that ${profile.brandName} is ${businessTypePhrase(profile.businessType)}. Does that look right?`;
  }
  if (stage === 1) return getOnboardingQuestion(stageFocus(stage, profile), profile).prompt;
  if (stage === 2 && hasStyle(profile)) {
    return `I’ll use a ${profile.voice.toLowerCase()} voice and build around ${profile.cadence.toLowerCase()}. Use this direction?`;
  }
  if (stage === 2 && profile.voice) {
    return `${profile.voice} gives me the voice. What publishing rhythm feels realistic?`;
  }
  if (stage === 2 && profile.cadence) {
    return `${profile.cadence} gives me the rhythm. How should the posts sound?`;
  }
  if (stage === 2) return getOnboardingQuestion('voice', profile).prompt;
  if (stage === 3) {
    return `I’ve got ${profile.brandName || 'your brand'}: ${profile.businessType || 'your organization'}, focused on ${profile.goal || 'your first goal'}. Every connection and post stays under your approval.`;
  }
  return '';
}

function stageSuggestions(stage: Stage, profile: OnboardingProfile) {
  if (stage === 1 && hasBrand(profile)) return [LOOKS_RIGHT];
  if (stage === 2 && hasStyle(profile)) return [USE_DIRECTION];
  return getOnboardingQuestion(stageFocus(stage, profile), profile).suggestions;
}

function composerPlaceholder(stage: Stage, profile: OnboardingProfile) {
  if (stage === 0) return 'Tell us in your own words…';
  if (stage === 1) return 'Tell me about the brand…';
  if (stage === 2) return 'Tell me how it should feel…';
  return getOnboardingQuestion(stageFocus(stage, profile), profile).placeholder;
}

export function Onboarding({ onComplete, onTurn }: OnboardingProps) {
  const [showSplash, setShowSplash] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [stage, setStage] = useState<Stage>(0);
  const [focus, setFocus] = useState<OnboardingFocus>('goal');
  const [answer, setAnswer] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [editingFromReview, setEditingFromReview] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>(
    getOnboardingQuestion('goal', {}).suggestions,
  );
  const [profile, setProfile] = useState<OnboardingProfile>(initialProfile);
  const [history, setHistory] = useState<OnboardingMessage[]>([]);

  useEffect(() => {
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    const dismiss = () => setShowSplash(false);
    const timer = window.setTimeout(dismiss, reducedMotion ? 250 : 2_200);
    const skipWithKeyboard = (event: KeyboardEvent) => {
      if (event.key === 'Enter') dismiss();
    };
    window.addEventListener('pointerdown', dismiss, { once: true });
    window.addEventListener('keydown', skipWithKeyboard);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('pointerdown', dismiss);
      window.removeEventListener('keydown', skipWithKeyboard);
    };
  }, []);

  const latestAssistant = useMemo(
    () => [...history].reverse().find((line) => line.role === 'assistant')?.text,
    [history],
  );

  function setAssistantMessage(userText: string, assistantText: string) {
    setHistory((existing) => [
      ...existing,
      { role: 'user', text: userText },
      { role: 'assistant', text: assistantText },
    ].slice(-12) as OnboardingMessage[]);
  }

  function advanceConfirmedStage(choice: string) {
    if (stage === 1 && choice === LOOKS_RIGHT && hasBrand(profile)) {
      const nextStage: Stage = 2;
      const nextMessage = stageAssistant(nextStage, profile);
      setStage(nextStage);
      setFocus(stageFocus(nextStage, profile));
      setSuggestions(stageSuggestions(nextStage, profile));
      setAssistantMessage(choice, nextMessage);
      setAnswer('');
      return true;
    }
    if (stage === 2 && choice === USE_DIRECTION && hasStyle(profile)) {
      const nextStage: Stage = 3;
      const nextMessage = stageAssistant(nextStage, profile);
      setStage(nextStage);
      setFocus('review');
      setSuggestions([]);
      setReviewing(true);
      setAssistantMessage(choice, nextMessage);
      setAnswer('');
      return true;
    }
    return false;
  }

  async function submit(value = answer) {
    const clean = value.trim();
    if (!clean || busy || reviewing) return;
    if (advanceConfirmedStage(clean)) return;
    setBusy(true);
    setError(undefined);

    const request: OnboardingTurnRequest = {
      message: clean,
      profile,
      history: history.slice(-8),
    };

    try {
      const result = onTurn
        ? await onTurn(request)
        : interpretDeterministicTurn(request, focus);
      const nextProfile = normalizeProfile({ ...profile, ...result.profile });

      let nextStage = stage;
      let nextFocus = focus;
      let shouldReview = false;
      let assistantMessage = result.message.trim();

      if (editingFromReview) {
        nextStage = 3;
        nextFocus = 'review';
        shouldReview = true;
        assistantMessage = stageAssistant(3, withRecommendedDefaults(nextProfile));
        setEditingFromReview(false);
      } else if (stage === 0) {
        nextStage = 1;
        nextFocus = stageFocus(nextStage, nextProfile);
        assistantMessage = stageAssistant(nextStage, nextProfile);
      } else if (stage === 1 && hasBrand(nextProfile)) {
        nextStage = 2;
        nextFocus = stageFocus(nextStage, nextProfile);
        assistantMessage = stageAssistant(nextStage, nextProfile);
      } else if (stage === 2 && hasStyle(nextProfile)) {
        nextStage = 3;
        nextFocus = 'review';
        shouldReview = true;
        assistantMessage = stageAssistant(nextStage, nextProfile);
      } else {
        nextFocus = stageFocus(nextStage, nextProfile);
        assistantMessage ||= stageAssistant(nextStage, nextProfile);
      }

      const displayProfile = shouldReview ? withRecommendedDefaults(nextProfile) : nextProfile;
      setProfile(displayProfile);
      setStage(nextStage);
      setFocus(nextFocus);
      setReviewing(shouldReview);
      setSuggestions(shouldReview ? [] : stageSuggestions(nextStage, displayProfile));
      setAssistantMessage(clean, assistantMessage);
      setAnswer('');
    } catch (reason) {
      setError(reason instanceof Error
        ? reason.message
        : 'I could not save that answer. It is still here—please try again.');
    } finally {
      setBusy(false);
    }
  }

  function editField(field: OnboardingField) {
    if (busy || saving) return;
    const question = getOnboardingQuestion(field, profile);
    const editStage: Stage = field === 'goal' ? 0 : field === 'brandName' || field === 'businessType' ? 1 : 2;
    setReviewing(false);
    setEditingFromReview(true);
    setStage(editStage);
    setFocus(field);
    setSuggestions(question.suggestions);
    setAnswer(profile[field]);
    setError(undefined);
    setHistory((existing) => [
      ...existing,
      { role: 'assistant', text: question.prompt },
    ].slice(-12) as OnboardingMessage[]);
  }

  async function finish() {
    setSaving(true);
    setError(undefined);
    try {
      await onComplete(withRecommendedDefaults(profile));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Your workspace could not be created.');
    } finally {
      setSaving(false);
    }
  }

  const benefitCards = [
    {
      label: 'Brand brief ready',
      detail: hasBrand(profile) ? `${profile.brandName} · ${profile.businessType}` : 'Your business and audience in one place.',
      unlocked: hasBrand(profile),
      field: 'brandName' as OnboardingField,
      Icon: Type,
    },
    {
      label: 'Content direction unlocked',
      detail: profile.goal || 'Ideas shaped around your goal.',
      unlocked: Boolean(profile.goal),
      field: 'goal' as OnboardingField,
      Icon: Target,
    },
    {
      label: 'Voice guide ready',
      detail: profile.voice || 'Content that sounds like your brand.',
      unlocked: Boolean(profile.voice),
      field: 'voice' as OnboardingField,
      Icon: AudioLines,
    },
    {
      label: 'Publishing plan ready',
      detail: profile.cadence || 'A realistic rhythm you approve.',
      unlocked: Boolean(profile.cadence),
      field: 'cadence' as OnboardingField,
      Icon: CalendarClock,
    },
  ];

  const stageCopy = STAGES[stage];

  return (
    <div className={`onboarding-shell ${showSplash ? 'is-splashing' : ''}`}>
      {showSplash && (
        <div className="onboarding-splash" role="status" aria-label="ThisBot is getting your setup ready">
          <div className="onboarding-splash__lockup">
            <img src="/brand/assistant-mark.png" alt="" />
            <div><strong>ThisBot</strong><span>DoesMySocialMedia</span></div>
            <i aria-hidden="true" />
          </div>
        </div>
      )}

      <div className="onboarding-flow" aria-hidden={showSplash || undefined}>
        <header className="onboarding-header">
          <div className="onboarding-brand-lockup">
            <Brand compact />
            <span>doesmysocialmedia.com</span>
          </div>
        </header>

        <main className="onboarding-main">
          <div className="onboarding-step-row" aria-label={`Step ${stage + 1} of 4: ${stageCopy.label}`}>
            <span className="onboarding-step-pill">Step {stage + 1} of 4</span>
            <div className="onboarding-step-dots" aria-hidden="true">
              {STAGES.map((item, index) => (
                <i key={item.label} className={index < stage ? 'is-done' : index === stage ? 'is-current' : ''} />
              ))}
            </div>
          </div>

          <section className="onboarding-question">
            <div>
              <h1>{stageCopy.title}</h1>
              <p>{stageCopy.subtitle}</p>
            </div>
            <img src="/brand/assistant-mark.png" alt="" />
          </section>

          {(latestAssistant || busy) && (
            <div className={`onboarding-response ${busy ? 'is-typing' : ''}`} aria-live="polite" aria-busy={busy}>
              <img src="/brand/assistant-mark.png" alt="" />
              {busy ? (
                <p><span /><span /><span /><span className="visually-hidden">ThisBot is working</span></p>
              ) : <p>{latestAssistant}</p>}
            </div>
          )}

          {!reviewing && !busy && suggestions.length > 0 && (
            <div className="onboarding-choices" aria-label="Suggested answers">
              {suggestions.map((choice) => (
                <button type="button" key={choice} onClick={() => void submit(choice)}>
                  {choice}<ArrowRight size={14} />
                </button>
              ))}
            </div>
          )}

          {!reviewing ? (
            <div className={`onboarding-composer ${error ? 'has-error' : ''}`}>
              <MessageSquareText size={19} />
              <textarea
                rows={1}
                value={answer}
                aria-label="Your answer"
                placeholder={composerPlaceholder(stage, profile)}
                onChange={(event) => setAnswer(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    void submit();
                  }
                }}
                disabled={busy}
                autoFocus={!showSplash}
              />
              <button disabled={busy || !answer.trim()} onClick={() => void submit()} aria-label="Send answer">
                <ArrowRight size={22} />
              </button>
            </div>
          ) : (
            <button className="onboarding-start-chat" disabled={saving} onClick={() => void finish()}>
              <span><Sparkles size={18} />{saving ? 'Opening your conversation…' : 'Start chatting with ThisBot'}</span>
              <ArrowRight size={22} />
            </button>
          )}

          {error && <p className="inline-warning onboarding-error" role="alert">{error}</p>}

          <p className="onboarding-privacy">
            <LockKeyhole size={13} /> Your information is private and secure. Nothing is published during setup.
          </p>

          <div className="onboarding-benefit-grid" aria-label="Your setup progress">
            {benefitCards.map(({ label, detail, unlocked, field, Icon }) => (
              <article key={label} className={`onboarding-benefit-card ${unlocked ? 'is-unlocked' : ''}`}>
                <span className="onboarding-benefit-icon"><Icon size={20} /></span>
                <span className="onboarding-benefit-status">{unlocked ? <><Check size={12} /> Ready</> : 'Up next'}</span>
                <strong>{label}</strong>
                <p>{detail}</p>
                {reviewing && unlocked && (
                  <button type="button" onClick={() => editField(field)} aria-label={`Edit ${label}`}>
                    <PenLine size={11} /> Edit
                  </button>
                )}
              </article>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
}
